import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { ToastItem, ToastOptions } from '../types';

import { ToastContext, type ToastContextValue } from '../context';
import {
  getAutoDismissDuration,
  resolveToastId,
  shouldAutoDismiss,
} from '../utils';
import { ToastViewport } from './ToastViewport';

type ToastProviderProps = {
  children: ReactNode;
};

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const dismissTimers = useRef(
    new Map<string, ReturnType<typeof setTimeout>>(),
  );

  const clearDismissTimer = useCallback((id: string) => {
    const timer = dismissTimers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      dismissTimers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id?: string) => {
      if (id === undefined) {
        dismissTimers.current.forEach((timer) => clearTimeout(timer));
        dismissTimers.current.clear();
        setToasts([]);
        return;
      }

      clearDismissTimer(id);
      setToasts((current) => current.filter((item) => item.id !== id));
    },
    [clearDismissTimer],
  );

  const scheduleDismiss = useCallback(
    (toast: ToastItem) => {
      if (!shouldAutoDismiss(toast)) {
        return;
      }

      const duration = getAutoDismissDuration(toast.text, toast.title);
      const timer = setTimeout(() => dismiss(toast.id), duration);
      dismissTimers.current.set(toast.id, timer);
    },
    [dismiss],
  );

  const enqueue = useCallback(
    (options: ToastOptions) => {
      const id = resolveToastId(options.id);
      const nextToast: ToastItem = { ...options, id };

      setToasts((current) => {
        const withoutDuplicate = current.filter((toast) => toast.id !== id);
        return [...withoutDuplicate, nextToast];
      });

      clearDismissTimer(id);
      scheduleDismiss(nextToast);

      return id;
    },
    [clearDismissTimer, scheduleDismiss],
  );

  useEffect(
    () => () => {
      dismissTimers.current.forEach((timer) => clearTimeout(timer));
      dismissTimers.current.clear();
    },
    [],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      dismiss,
      enqueue,
      toasts,
    }),
    [dismiss, enqueue, toasts],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport onDismiss={dismiss} toasts={toasts} />
    </ToastContext.Provider>
  );
};
